import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import { useSelector } from 'react-redux';
import Participation from '../../../common/models/Participation';
import { default as ProjectSelect } from '../../../common/selectors/Project';
import { default as RecipientSelect } from '../../../common/selectors/Recipient';
import { apiZedocOneProject } from '../../../common/api/zedoc';
import ConfirmAction from '../../dialogs/ConfirmAction';
import FormFieldWrapper from '../../forms/FormFieldWrapper';
import Select from '../../Select';
import { notifySuccess } from '../../../utils/notify';

const NotificationPreferencesAllProjects = ({
  enableNotifications,
  recipient,
  notificationMethodPreference,
}) => {
  const { t } = useTranslation();
  const phone = useMemo(() => recipient?.getPhoneNumberMobile(), [recipient]);
  const email = useMemo(() => recipient?.getEmailPersonal(), [recipient]);

  const currentMethod = useMemo(() => {
    let method = recipient && recipient?.preferredNotificationMethod;
    return method || 'default';
  }, [recipient]);

  const [selectedMethod, setSelectedMethod] = useState('');
  const notificationPreferencesOptions = useMemo(
    () =>
      [
        {
          label: t('email'),
          value: 'email',
        },
        {
          label: t('sms'),
          value: 'sms',
        },
        {
          label: t('both'),
          value: 'both',
        },
        {
          label: t('default'),
          value: 'default',
        },
      ].filter((option) => {
        if (option.value === 'email' && !email) {
          return false;
        }
        if (option.value === 'sms' && !phone) {
          return false;
        }
        if (option.value === 'both' && (!phone || !email)) {
          return false;
        }
        return true;
      }),
    [email, phone, t],
  );

  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const handleOnOpenDialog = () => setIsDialogVisible(true);
  const handleOnCloseDialog = () => setIsDialogVisible(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleOnChangeMethod = (method) => {
    setSelectedMethod(method);
    handleOnOpenDialog();
  };
  const handleOnDialogConfirm = () => {
    setIsLoading(true);
    notificationMethodPreference(selectedMethod);
    handleOnCloseDialog();
    notifySuccess(
      t('confirmations:notificationPreferencesAllProjects.success', {
        method: selectedMethod.toUpperCase(),
      }),
    )();
    setIsLoading(false);
  };

  return !recipient ? null : (
    <>
      <FormFieldWrapper
        label={t('forms:notificationPreferencesForAllProjects.label')}
        disabled={!enableNotifications}
      >
        <Select
          options={notificationPreferencesOptions}
          value={currentMethod}
          onChange={handleOnChangeMethod}
          isDisabled={!enableNotifications}
        />
      </FormFieldWrapper>
      <ConfirmAction
        message={t('confirmations:notificationPreferencesAllProjects.message', {
          method: selectedMethod.toUpperCase(),
        })}
        open={isDialogVisible}
        onConfirm={handleOnDialogConfirm}
        onCancel={handleOnCloseDialog}
        loading={isLoading}
      />
    </>
  );
};

NotificationPreferencesAllProjects.propTypes = {
  participation: PropTypes.instanceOf(Participation).isRequired,
  enableNotifications: PropTypes.bool,
};

NotificationPreferencesAllProjects.defaultProps = {
  enableNotifications: false,
};

export default NotificationPreferencesAllProjects;
